import React from 'react'
import { useTranslation } from 'react-i18next'
import Highcharts from 'highcharts'
import HighchartsExporting from 'highcharts/modules/exporting'
import HighchartsReact from 'highcharts-react-official'
import { useTheme } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import Fullscreen from '@mui/icons-material/Fullscreen'
import PictureAsPdf from '@mui/icons-material/PictureAsPdf'
import ListAltIcon from '@mui/icons-material/ListAlt'

import { EventType, EventTypeString } from '../../types/enums/eventType'
import { GraphSeriesConfig, GraphSeriesEvents } from '../../types/graphSeriesConfig'
import {
  BasicDataParam,
  CalculatedDataParam,
  GraphDataParam,
  SensorconfigParam,
} from '../../types/enums/graphDataParam'
import {
  AxisConfig,
  convertToTempScale,
  getDisplayTemperature,
  getFullscreenElement,
  getGraphLimits,
  getMaxChartDatapoints,
  getPlotLineConfig,
  getValueSuffix,
  getXaxis,
  getYaxis,
  hexToRGBA,
  indexToColor,
  isFullscreenEnabled,
} from '../../utils/graphUtils'
import { calculateTimeDiff, msToTime } from '../../utils/dateUtils'
import TooltipIconButton from '../fields/tooltipIconButton'
import { SeriesType } from '../../types/enums/seriesType'
import { getHiddenChartSeries, setHiddenChartSeries } from '../../utils/localStorageUtils'

// initialize highcharts export
HighchartsExporting(Highcharts)

const TOP_CHART_TOP = 4 // this height is in px

// all following heights are in %.
const TOP_CHART_DEFAULT_HEIGHT = 75
const BOTTOM_CHART_HEIGHT = 25
const MODULATION_CHART_HEIGHT = 5

interface ChartProps {
  chartMaxTemp: number
  exportTitle: string
  eventDatapoints: Array<GraphSeriesEvents>
  seriesConfig: Array<GraphSeriesConfig>
  compareLogSeries: Array<GraphSeriesConfig>
  expectedSeries: Array<GraphSeriesConfig>
  averageSeries?: Array<GraphSeriesConfig>
  livePlotlineMsec?: number
  compareFocusLogId?: number
  resizeCount?: number // used to trigger reflow of chart size from outside component
  rorInterval: number
  downloadCsvButton?: JSX.Element
  showProfileDataButton?: JSX.Element
  storeSeriesVisiblity?: boolean
  onRequestFullscreen: () => void
}

const LogChart = (props: ChartProps): JSX.Element => {
  const { t } = useTranslation()
  const theme = useTheme()

  const [chart, setChart] = React.useState<Highcharts.Chart | null>(null)
  const [isSharedTooltip, setIsSharedTooltip] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (chart && chart.options && props.resizeCount) {
      chart.reflow()
    }
  }, [chart, props.resizeCount])

  const getMinRoRValue = (): number => {
    let ignoreBeforeTimestamp = 60000
    const turningPointTimestamp = getEventTimestamp(EventType.TurningPoint)
    if (turningPointTimestamp > 0) {
      ignoreBeforeTimestamp = turningPointTimestamp + props.rorInterval * 1000
    }
    let minRoR: number = 0
    props.seriesConfig.forEach((series) => {
      if (series.seriesType === SeriesType.ROR && getSeriesVisibility(series)) {
        const validDataPoints = series.datapoints.filter((point) => point.msec >= ignoreBeforeTimestamp)
        if (validDataPoints.length > 0) {
          const minSeriesRoR = Math.min(...validDataPoints.map((point) => point.dataValue || 0))
          minRoR = Math.min(minRoR, minSeriesRoR)
        }
      }
    })
    return isFinite(minRoR) ? minRoR : 0
  }

  const titleCallback = React.useCallback(
    (node: HighchartsReact.RefObject) => {
      if (setChart && node) {
        setChart(node.chart)
      }
    },
    [setChart]
  )

  const hasXaxis = (axisId: string): boolean => {
    return (
      props.seriesConfig.filter((x) => getXaxis(x.seriesType) === axisId).length > 0 ||
      props.compareLogSeries.filter((x) => getXaxis(x.seriesType) === axisId).length > 0 ||
      (props.seriesConfig.length === 0 && axisId === AxisConfig.X_AXIS_TOP)
    )
  }

  const hasYaxis = (axisId: string): boolean => {
    return (
      props.seriesConfig.filter((x) => getYaxis(x.seriesType) === axisId).length > 0 ||
      props.compareLogSeries.filter((x) => getYaxis(x.seriesType) === axisId).length > 0 ||
      (props.seriesConfig.length === 0 && axisId === AxisConfig.Y_AXIS_TEMPERATURE)
    )
  }

  const getTopChartHeight = (): number => {
    return TOP_CHART_DEFAULT_HEIGHT - props.eventDatapoints.length * MODULATION_CHART_HEIGHT
  }

  const getTopChartConfig = (): Highcharts.XAxisOptions => {
    return {
      top: hasXaxis(AxisConfig.X_AXIS_BOTTOM) ? TOP_CHART_TOP : undefined,
      height: hasXaxis(AxisConfig.X_AXIS_BOTTOM) ? `${getTopChartHeight()}%` : undefined,
      offset: 0,
    }
  }

  const getBottomChartConfig = (): Highcharts.XAxisOptions => {
    return {
      top: hasXaxis(AxisConfig.X_AXIS_TOP) ? `${getTopChartHeight()}%` : undefined,
      height: hasXaxis(AxisConfig.X_AXIS_TOP) ? `${BOTTOM_CHART_HEIGHT}%` : undefined,
      offset: 0,
    }
  }

  const getModulationChartConfig = (index: number): Highcharts.XAxisOptions => {
    return {
      linkedTo: hasXaxis(AxisConfig.X_AXIS_TOP) ? 0 : undefined,
      height: `${MODULATION_CHART_HEIGHT}%`,
      top: `${getTopChartHeight() + BOTTOM_CHART_HEIGHT + index * MODULATION_CHART_HEIGHT} %`,
      offset: 0,
    }
  }

  const getXaxisConfig = (): Highcharts.XAxisOptions => {
    return {
      min: 0,
      max: getMaxChartDatapoints([...props.compareLogSeries, ...props.seriesConfig, ...props.expectedSeries]),
      tickPixelInterval: 60,
      type: 'datetime',
      ordinal: false,
      dateTimeLabelFormats: {
        second: '%M:%S',
        minute: '%M:%S',
        hour: '%M:%S',
        day: '%M:%S',
      },
      crosshair: {
        color: theme.palette.primary.main,
        dashStyle: 'ShortDot',
        width: 2,
      },
    }
  }

  const getSeriesVisibility = (series: GraphSeriesConfig): boolean => {
    const isVisible = chart && chart.series && chart.series.find((x) => x.options.id === series.seriesId)?.visible
    const defaultVisibility = getHiddenChartSeries().indexOf(series.seriesId) > -1 ? false : true
    return isVisible === undefined || isVisible === null ? defaultVisibility : isVisible
  }

  // TODO: this will not handle sensorconfig series which are related to BasicDataParam.
  // F.ex the extra drum sensors of P2000 should be grouped with BasicDataParam.DT.
  const getTooltipOrder = (param: GraphDataParam): number => {
    const order = [
      BasicDataParam.FAN,
      BasicDataParam.RPM,
      BasicDataParam.POWER,
      BasicDataParam.TARGET,
      BasicDataParam.BT,
      BasicDataParam.ET,
      CalculatedDataParam.ROR,
      CalculatedDataParam.ET_ROR,
      CalculatedDataParam.EXT_ROR,
      BasicDataParam.CRACK,
      CalculatedDataParam.TOTAL_CRACKS,
      BasicDataParam.PCB_TEMP,
      BasicDataParam.DRUM,
      BasicDataParam.INLET,
      BasicDataParam.DRUM_PRESSURE,
      SensorconfigParam.RTD0,
      SensorconfigParam.TC0,
      SensorconfigParam.TC1,
      SensorconfigParam.TC2,
      SensorconfigParam.TC3,
      SensorconfigParam.TC4,
      SensorconfigParam.TC5,
      SensorconfigParam.TC6,
      SensorconfigParam.TC7,
      SensorconfigParam.TC8,
      SensorconfigParam.TC9,
      SensorconfigParam.TC10,
      SensorconfigParam.TC11,
      SensorconfigParam.TC12,
      SensorconfigParam.RTD0,
      SensorconfigParam.RTD1,
      SensorconfigParam.RTD2,
      SensorconfigParam.RTD3,
      SensorconfigParam.RTD4,
      SensorconfigParam.RTD5,
      SensorconfigParam.RTD6,
      SensorconfigParam.RTD7,
      SensorconfigParam.RTD8,
      SensorconfigParam.RTD9,
      SensorconfigParam.RTD10,
      SensorconfigParam.RTD11,
    ]

    return order.indexOf(param)
  }

  const getCommonSeriesConfig = (
    series: GraphSeriesConfig,
    enableLegendEvents?: boolean
  ):
    | Highcharts.SeriesLineOptions
    | Highcharts.SeriesSplineOptions
    | Highcharts.SeriesAreaOptions
    | Highcharts.SeriesColumnOptions => {
    const isCompareFocusLogId = props.compareFocusLogId && series.logId === props.compareFocusLogId
    let chartType: 'spline' | 'area' | 'column' = 'spline'
    if (series.param === BasicDataParam.CRACK) {
      chartType = 'column'
    } else if (series.param === CalculatedDataParam.TOTAL_CRACKS) {
      chartType = 'area'
    }

    const isTemp = series.seriesType === SeriesType.TEMPERATURE || series.seriesType === SeriesType.ROR
    return {
      id: series.seriesId,
      name: t(series.seriesLabel),
      color: series.color,
      fillColor: getSeriesFillColor(series.param),
      index: getTooltipOrder(series.param),
      xAxis: getXaxis(series.seriesType),
      yAxis: getYaxis(series.seriesType),
      type: chartType,
      tooltip: {
        valueDecimals: isTemp ? 2 : 0,
        valueSuffix: getValueSuffix(series.seriesType),
      },
      visible: getSeriesVisibility(series),
      lineWidth: isCompareFocusLogId ? 4 : 2,
      opacity:
        (props.averageSeries && props.averageSeries.length > 0) || (props.compareFocusLogId && !isCompareFocusLogId)
          ? 0.3
          : 1,
      data: series.datapoints.map((x) => [
        x.msec,
        isTemp && x.dataValue !== null ? convertToTempScale(x.dataValue, 2, series.param) : x.dataValue,
      ]),
      // allow hiding predicted-target-<param>, expected-target-<param> and <param> series:
      ...(enableLegendEvents && {
        events: {
          hide: () => {
            setHiddenChartSeries([...getHiddenChartSeries(), series.seriesId])
            updateYAxisRoR()
          },
          show: () => {
            setHiddenChartSeries(getHiddenChartSeries().filter((x) => x !== series.seriesId))
            updateYAxisRoR()
          },
        },
      }),
    }
  }
  const updateYAxisRoR = (): void => {
    var minValue = getMinRoRValue()
    chart?.yAxis[3].update({
      min: minValue,
    })
  }

  const getTempInLastMsec = (param: BasicDataParam.BT | BasicDataParam.ET): string => {
    const lastTempValue = props.seriesConfig
      .find((x) => x.param === param)
      ?.datapoints.find((x) => x.msec === props.livePlotlineMsec)?.dataValue

    const tempString = param === BasicDataParam.BT ? t('logview.beantemp') : t('logview.airtemp')
    return lastTempValue ? `${tempString}: ${getDisplayTemperature(lastTempValue, 1)}` : ''
  }

  const getEventTimestamp = (event: EventType): number => {
    const logEvents = props.eventDatapoints.filter((x) => x.labelPrefix === '')
    return logEvents.length > 0 ? logEvents[0].events.find((x) => x.type === event)?.msec ?? 0 : 0
  }

  const getLivePlotlineLabelText = (): string => {
    const totalCracks = props.seriesConfig
      .find((x) => x.param === BasicDataParam.CRACK)
      ?.datapoints.reduce((acc, curr) => acc + (curr.dataValue || 0), 0)
    const firstCracktimestamp = getEventTimestamp(EventType.Firstcrack)

    const timeFragment = `${t('logview.time')}: ${msToTime(props.livePlotlineMsec || 0)}`
    const cracksFragment = !!totalCracks ? `${t('logview.cracks')}: ${totalCracks}` : ''
    const etTempFragment = getTempInLastMsec(BasicDataParam.ET)
    const btTempFragment = getTempInLastMsec(BasicDataParam.BT)
    const developmentTimeFragment =
      props.livePlotlineMsec && firstCracktimestamp
        ? `${t('logsview.developmenttime')}: ${calculateTimeDiff(
            new Date(props.livePlotlineMsec),
            new Date(firstCracktimestamp)
          )} ${calculateStagePercent(props.livePlotlineMsec, firstCracktimestamp, props.livePlotlineMsec)}`
        : ''
    return [timeFragment, etTempFragment, btTempFragment, cracksFragment, developmentTimeFragment]
      .filter(Boolean)
      .join('<br/>')
  }

  const getPlotLinesConfig = (showLabel: boolean): Array<Highcharts.XAxisPlotLinesOptions> => {
    const plotLines: Array<Highcharts.XAxisPlotLinesOptions> = []

    props.eventDatapoints.forEach((graphSeriesEvents, index) => {
      graphSeriesEvents.events.forEach((x) => {
        const eventTemperature = getDisplayTemperature(x.bt, 1)
        const temperatureFragment = eventTemperature ? ` (${eventTemperature})` : ''
        const text =
          x.type !== null ? `${graphSeriesEvents.labelPrefix} ${t(EventTypeString[x.type])}${temperatureFragment}` : ''
        const opacity = props.compareFocusLogId && props.compareFocusLogId !== graphSeriesEvents.logId ? 0.3 : 1

        const color = graphSeriesEvents.labelPrefix
          ? hexToRGBA(theme.palette.plotline[indexToColor(index)] || '', opacity)
          : hexToRGBA(theme.palette.plotline.dark, opacity)

        plotLines.push(
          ...getPlotLineConfig(
            x.msec,
            color,
            showLabel ? text : '',
            `${x.type}-${graphSeriesEvents.labelPrefix}`,
            theme,
            opacity
          )
        )
      })
    })

    if (props.livePlotlineMsec) {
      plotLines.push({
        value: props.livePlotlineMsec,
        color: theme.palette.secondary.main,
        dashStyle: 'ShortDash',
        width: 2,
        id: 'live-plotline',
        zIndex: 4,
        ...(showLabel && {
          label: {
            useHTML: true,
            x: 10,
            y: 20,
            rotation: 0,
            text: getLivePlotlineLabelText(),
            style: {
              padding: '0 5px',
              backgroundColor: hexToRGBA(theme.palette.background.default, 0.8),
              color: theme.palette.text.primary, // use default colors from MUI (easier than separate config for plotlines)
            },
          },
        }),
      })
    }

    return plotLines
  }

  const calculateStagePercent = (totalTime: number | undefined, stageStart: number, stageEnd: number): string => {
    return totalTime ? `(${((100 * (stageEnd - stageStart)) / totalTime).toFixed(2)}%)` : ''
  }

  const getSeriesFillColor = (param: GraphDataParam): string | undefined => {
    return param === CalculatedDataParam.TOTAL_CRACKS ? hexToRGBA(theme.palette.cracks.main, 0.1) : undefined
  }

  const createPlotBandConfig = (
    from: number,
    to: number,
    totalTime: number | undefined,
    color: string,
    prefix: string,
    opacity: number
  ): Highcharts.XAxisPlotBandsOptions => {
    const stagePercent = calculateStagePercent(totalTime, from, to)
    return {
      from: from,
      to: to,
      color: hexToRGBA(color, opacity),
      zIndex: 1,
      borderWidth: 1,
      borderColor: theme.palette.background.default,
      label: {
        text: `${prefix} ${calculateTimeDiff(new Date(to), new Date(from))} ${stagePercent}`,
        align: 'left',
        x: 5,
        y: 5,
        verticalAlign: 'middle',
        style: {
          opacity: opacity,
          color: theme.palette.text.secondary,
        },
      },
    }
  }

  const getPlotBandsConfig = (graphSeriesEvents: GraphSeriesEvents): Array<Highcharts.XAxisPlotBandsOptions> => {
    const opacity = props.compareFocusLogId && props.compareFocusLogId !== graphSeriesEvents.logId ? 0.3 : 1
    // find timestamps: yellowing, first crack, drop:
    const dryendMsec = graphSeriesEvents.events.find((x) => x.type === EventType.Dryend)?.msec || 0
    const firstcrackMsec = graphSeriesEvents.events.find((x) => x.type === EventType.Firstcrack)?.msec || 0
    const dropMsec =
      graphSeriesEvents.events.find((x) => x.type === EventType.Drop)?.msec || props.livePlotlineMsec || 0

    const toMsec = dryendMsec || firstcrackMsec || props.livePlotlineMsec || 0
    const plotBandConfig: Array<Highcharts.XAxisPlotBandsOptions> = [
      createPlotBandConfig(0, toMsec, dropMsec, theme.palette.dryingStage.main, graphSeriesEvents.labelPrefix, opacity),
    ]

    if (dryendMsec) {
      const toEventMsec = firstcrackMsec || dropMsec || props.livePlotlineMsec || 0
      plotBandConfig.push(
        createPlotBandConfig(dryendMsec, toEventMsec, dropMsec, theme.palette.maillardStage.main, '', opacity)
      )
    }
    if (firstcrackMsec) {
      const toEventMsec = dropMsec || props.livePlotlineMsec || 0
      plotBandConfig.push(
        createPlotBandConfig(firstcrackMsec, toEventMsec, dropMsec, theme.palette.secondary.main, '', opacity)
      )
    }

    return plotBandConfig
  }

  const options = {
    title: {
      text: '',
      useHTML: true,
    },
    subtitle: {
      text: '',
      useHTML: true,
      y: 32,
    },
    // rules for small screens:
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 400,
          },
          chartOptions: {
            chart: {
              spacing: [10, -5, 0, -5],
            },
          },
        },
        {
          condition: {
            maxHeight: 350,
          },
          chartOptions: {
            chart: {
              spacing: [10, -5, -5, -5],
            },
            legend: {
              align: 'right',
              layout: 'vertical',
              verticalAlign: 'top',
              y: 25,
              itemStyle: {
                width: 100,
                textOverflow: undefined,
              },
            },
          },
        },
      ],
    },
    chart: { type: 'line', zoomType: 'xy', panning: true, panKey: 'shift' },
    accessibility: {
      enabled: false,
    },
    credits: { enabled: false },
    exporting: {
      filename: props.exportTitle,
      spacing: [10, 10, 10, 10],
      scale: 1,
      sourceWidth: 1061,
      sourceHeight: 750,
      buttons: {
        contextButton: {
          enabled: false,
        },
      },
    },
    tooltip: {
      xDateFormat: `${t('logview.time')}: %M:%S`,
      split: !isSharedTooltip,
      shared: isSharedTooltip,
      followPointer: true,
      findNearestPointBy: 'y',
      borderWidth: 0,
    },
    xAxis: [
      {
        ...getTopChartConfig(),
        ...getXaxisConfig(),
        id: AxisConfig.X_AXIS_TOP,
        visible: hasXaxis(AxisConfig.X_AXIS_TOP),
        labels: {
          y: 25,
          enabled: hasXaxis(AxisConfig.X_AXIS_BOTTOM) ? false : true,
        },
        plotLines: getPlotLinesConfig(true),
      },
      {
        ...getBottomChartConfig(),
        ...getXaxisConfig(),
        linkedTo: hasXaxis(AxisConfig.X_AXIS_TOP) ? 0 : undefined,
        id: AxisConfig.X_AXIS_BOTTOM,
        visible: hasXaxis(AxisConfig.X_AXIS_BOTTOM),
        labels: {
          y: hasXaxis(AxisConfig.X_AXIS_TOP) ? -3 : 25,
        },
        opposite: hasXaxis(AxisConfig.X_AXIS_TOP) ? true : false,
        plotLines: getPlotLinesConfig(!hasXaxis(AxisConfig.X_AXIS_TOP)),
      },
      ...props.eventDatapoints.map((x, index) => {
        return {
          ...getModulationChartConfig(index),
          ...getXaxisConfig(),
          plotBands: getPlotBandsConfig(x),
          lineWidth: 0,
          tickLength: 0,
          labels: {
            enabled: false,
          },
        }
      }),
    ], //xAxis
    yAxis: [
      {
        // temperature series - yaxis = 0
        ...getTopChartConfig(),
        id: AxisConfig.Y_AXIS_TEMPERATURE,
        visible: hasYaxis(AxisConfig.Y_AXIS_TEMPERATURE) || hasYaxis(AxisConfig.Y_AXIS_ROR),
        min: getGraphLimits(BasicDataParam.ET).min,
        max: convertToTempScale(props.chartMaxTemp, 0),
        alignTicks: false,
        tickInterval: 25,
        title: { text: `${t('logview.temp')} (${getValueSuffix(SeriesType.TEMPERATURE)})`, margin: 15 },
      },
      {
        // Power & Fan - yaxis = 1
        ...getBottomChartConfig(),
        id: AxisConfig.Y_AXIS_PERCENT,
        visible: hasYaxis(AxisConfig.Y_AXIS_PERCENT),
        min: getGraphLimits(BasicDataParam.POWER).min,
        max: getGraphLimits(BasicDataParam.POWER).max,
        alignTicks: false,
        tickInterval: 25,
        title: { text: t('logview.powerandfan'), margin: 15 },
      },
      {
        // Pressure - yaxis = 2
        ...getBottomChartConfig(),
        id: AxisConfig.Y_AXIS_PRESSURE,
        visible: hasYaxis(AxisConfig.Y_AXIS_PRESSURE),
        opposite: true,
        min: getGraphLimits(BasicDataParam.DRUM_PRESSURE).min,
        max: getGraphLimits(BasicDataParam.DRUM_PRESSURE).max,
        alignTicks: false,
        tickInterval: 250,
        title: { text: `${t('logview.drumpressure')} (${getValueSuffix(SeriesType.PRESSURE)})`, margin: 15 },
      },
      {
        // Ror - yaxis = 3
        ...getTopChartConfig(),
        id: AxisConfig.Y_AXIS_ROR,
        visible: hasYaxis(AxisConfig.Y_AXIS_ROR),
        min: getMinRoRValue(),
        max: getGraphLimits(CalculatedDataParam.ROR).max + 5,
        gridLineWidth: 0,
        tickInterval: 10,
        alignTicks: false,
        opposite: true,
        decimals: true,
        title: {
          text: `${t('logview.rateofrise')} (${getValueSuffix(SeriesType.ROR)})`,
          margin: 15,
        },
        plotLines: [
          {
            value: getMinRoRValue() < 0 ? 0 : undefined,
            color: theme.palette.primary.main,
            dashStyle: 'ShortDot',
          },
        ],
      },
      {
        // Cracks - yaxis = 4
        ...getTopChartConfig(),
        id: AxisConfig.Y_AXIS_CRACKS,
        visible: false,
        min: 0,
        max: 33,
      },
      {
        // Total cracks - yaxis = 5
        ...getTopChartConfig(),
        id: AxisConfig.Y_AXIS_TOTAL_CRACKS,
        visible: false,
        min: 0,
        max: 200,
      },
      // Modulation charts - yaxis 5 and up
      ...props.eventDatapoints.map((_x, index: number) => {
        return {
          ...getModulationChartConfig(index),
          visible: false,
        }
      }),
    ],
    plotOptions: {
      series: {
        marker: { enabled: false },
        states: { inactive: { enabled: false } },
      },
      column: {
        borderWidth: 0,
      },
    },
    series: [
      ...(props.seriesConfig.length === 0
        ? [
            {
              id: 'empty-chart-placeholder-series',
              showInLegend: false,
              data: [],
            },
          ]
        : []),
      ...(props.averageSeries || []).map((series) => {
        return {
          ...getCommonSeriesConfig(series, false),
          dashStyle: 'shortdash',
          opacity: 1,
          legendIndex: 4,
        }
      }),
      ...props.expectedSeries.map((series) => {
        return {
          ...getCommonSeriesConfig(series, props.storeSeriesVisiblity),
          showInLegend: series.datapoints.length > 0 ? true : false,
          zones: props.livePlotlineMsec
            ? [
                {
                  value: props.livePlotlineMsec,
                  color: 'rgba(0, 0, 0, 0)',
                },
              ]
            : [],
          dashStyle: 'shortdash',
          zoneAxis: 'x',
          legendIndex: 1000, // show expected series last in legend
        }
      }),
      ...props.compareLogSeries.map((series) => {
        return {
          ...getCommonSeriesConfig(series, false),
          dashStyle: 'shortdot',
          legendIndex: 2,
        }
      }),
      ...props.seriesConfig.map((series) => {
        return {
          ...getCommonSeriesConfig(series, props.storeSeriesVisiblity),
          animation: series.param === BasicDataParam.CRACK ? false : undefined,
          legendIndex: 1,
        }
      }),
    ],
  }

  return (
    <Stack sx={{ position: 'relative', height: '100%', overflow: 'hidden', flex: 1 }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={titleCallback}
        containerProps={{ style: { height: '100%', flex: 1 } }}
      />
      {chart && (
        <Stack alignItems="center" sx={{ position: 'absolute', right: { xs: -8, sm: 0 }, top: '-5px' }}>
          {isFullscreenEnabled() && (
            <TooltipIconButton
              onClick={() => {
                if (getFullscreenElement()) {
                  document.exitFullscreen()
                } else {
                  props.onRequestFullscreen()
                }
              }}
              icon={<Fullscreen />}
              tooltip={t('logview.fullscreenview')}
            />
          )}
          {props.showProfileDataButton && props.showProfileDataButton}
          <TooltipIconButton
            onClick={() => setIsSharedTooltip((oldState) => !oldState)}
            icon={<ListAltIcon />}
            tooltip={isSharedTooltip ? t('logview.disablesharedtooltip') : t('logview.enablesharedtooltip')}
            isToggled={!!isSharedTooltip}
          />
          {props.downloadCsvButton && props.downloadCsvButton}
          <TooltipIconButton
            onClick={() => chart.exportChart({ type: 'application/pdf' }, {})}
            icon={<PictureAsPdf />}
            tooltip={t('logview.downloadpdf')}
          />
        </Stack>
      )}
    </Stack>
  )
}

export default LogChart
